import { apiGet, apiPost, apiPut } from './api-client'
import type { ToolId } from './access'

// Admin API — user invitations + per-tool access grants. Backed by the
// family-finance service (Cognito AdminCreateUser / group management). Falls
// back to a localStorage mock so the panel is fully usable before the
// backend is deployed.

const USE_MOCK = !import.meta.env.VITE_ADMIN_API_URL

let _usingMockData = USE_MOCK
export function isAdminUsingMockData(): boolean {
  return _usingMockData
}

export interface ManagedUser {
  email: string
  name: string
  status: 'invited' | 'active' | 'disabled'
  isAdmin: boolean
  tools: ToolId[]
  invitedAt: string
  lastSignIn: string | null
}

export interface InviteRequest {
  email: string
  name: string
  tools: ToolId[]
}

// ─── Mock store (localStorage-backed) ────────────────────────

const MOCK_KEY = 'nb-admin-mock-v1'

interface MockAdminState {
  users: ManagedUser[]
}

function seedState(): MockAdminState {
  return {
    users: [
      {
        email: 'nathanaelbrown@gmail.com',
        name: 'Nathanael Brown',
        status: 'active',
        isAdmin: true,
        tools: ['watch-monitor', 'financial-app', 'family-finance'],
        invitedAt: '2026-03-02T00:00:00Z',
        lastSignIn: new Date().toISOString(),
      },
    ],
  }
}

function loadMock(): MockAdminState {
  try {
    const raw = localStorage.getItem(MOCK_KEY)
    if (raw) return JSON.parse(raw) as MockAdminState
  } catch {
    // corrupted store — reseed
  }
  const state = seedState()
  saveMock(state)
  return state
}

function saveMock(state: MockAdminState) {
  try {
    localStorage.setItem(MOCK_KEY, JSON.stringify(state))
  } catch {
    // storage unavailable — mock persists for this page load only
  }
}

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms))

// ─── API functions ───────────────────────────────────────────

export async function listUsers(): Promise<ManagedUser[]> {
  if (!USE_MOCK) {
    try {
      const result = await apiGet<ManagedUser[]>('/api/admin/users')
      _usingMockData = false
      return result
    } catch {
      console.warn('Admin API unavailable, falling back to mock data')
      _usingMockData = true
    }
  }
  _usingMockData = true
  await delay(300)
  return loadMock().users
}

export async function inviteUser(req: InviteRequest): Promise<ManagedUser> {
  if (!USE_MOCK) {
    try {
      const result = await apiPost<ManagedUser>('/api/admin/users/invite', req)
      _usingMockData = false
      return result
    } catch {
      console.warn('Admin API unavailable, falling back to mock data')
      _usingMockData = true
    }
  }
  _usingMockData = true
  await delay(500)
  const state = loadMock()
  const email = req.email.trim().toLowerCase()
  if (state.users.some((u) => u.email === email)) {
    throw new Error(`${email} already has an account or pending invite.`)
  }
  const user: ManagedUser = {
    email,
    name: req.name.trim(),
    status: 'invited',
    isAdmin: false,
    tools: req.tools,
    invitedAt: new Date().toISOString(),
    lastSignIn: null,
  }
  state.users.push(user)
  saveMock(state)
  return user
}

export async function updateUserTools(email: string, tools: ToolId[]): Promise<ManagedUser> {
  if (!USE_MOCK) {
    try {
      const result = await apiPut<ManagedUser>(
        `/api/admin/users/${encodeURIComponent(email)}/tools`,
        { tools },
      )
      _usingMockData = false
      return result
    } catch {
      console.warn('Admin API unavailable, falling back to mock data')
      _usingMockData = true
    }
  }
  _usingMockData = true
  await delay(300)
  const state = loadMock()
  const user = state.users.find((u) => u.email === email.toLowerCase())
  if (!user) throw new Error(`No user found for ${email}`)
  user.tools = tools
  saveMock(state)
  return user
}

export async function setUserStatus(
  email: string,
  status: 'active' | 'disabled',
): Promise<ManagedUser> {
  if (!USE_MOCK) {
    try {
      const result = await apiPut<ManagedUser>(
        `/api/admin/users/${encodeURIComponent(email)}/status`,
        { status },
      )
      _usingMockData = false
      return result
    } catch {
      console.warn('Admin API unavailable, falling back to mock data')
      _usingMockData = true
    }
  }
  _usingMockData = true
  await delay(300)
  const state = loadMock()
  const user = state.users.find((u) => u.email === email.toLowerCase())
  if (!user) throw new Error(`No user found for ${email}`)
  user.status = status
  saveMock(state)
  return user
}

export async function resendInvite(email: string): Promise<void> {
  if (!USE_MOCK) {
    try {
      await apiPost<void>(`/api/admin/users/${encodeURIComponent(email)}/resend-invite`)
      _usingMockData = false
      return
    } catch {
      console.warn('Admin API unavailable, falling back to mock data')
      _usingMockData = true
    }
  }
  _usingMockData = true
  await delay(400)
}
